import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Box, Typography, TextField, Alert, CircularProgress, Tooltip, Chip, Stack,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { api } from '../../services/api';
import type { Organization, OrgApiKey } from '../../types';

interface Props {
  organization: Organization;
  onClose: () => void;
}

export default function OrgApiKeysDialog({ organization, onClose }: Props) {
  const [keys, setKeys] = useState<OrgApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScope, setNewKeyScope] = useState('driver-app');
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<{ id: string; key: string } | null>(null);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await api.getOrgApiKeys(organization.id);
      setKeys(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke hente nøgler');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [organization.id]);

  const create = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const created = await api.createOrgApiKey(organization.id, { name: newKeyName.trim(), scope: newKeyScope.trim() || 'driver-app' });
      setJustCreated({ id: created.id, key: created.key });
      setNewKeyName('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke oprette nøgle');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k: OrgApiKey) => {
    if (!window.confirm(`Tilbagekald nøglen "${k.name}"? Den kan ikke gendannes.`)) return;
    try {
      await api.revokeOrgApiKey(organization.id, k.id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke tilbagekalde nøgle');
    }
  };

  const copy = async (text: string, msg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMsg(msg);
      setTimeout(() => setCopyMsg(null), 2000);
    } catch {
      setCopyMsg('Kopi-rettighed afvist — marker og kopiér manuelt');
    }
  };

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  const activeKeys = keys.filter(k => !k.revokedAt);
  const revokedKeys = keys.filter(k => k.revokedAt);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        API-nøgler — {organization.name}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Bruges af eksterne systemer (rental-app, integrationer) via header <code>x-api-key</code>.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {copyMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCopyMsg(null)}>{copyMsg}</Alert>}

        {/* Nyligt oprettet nøgle — vis klart at klar-tekst kun vises NU */}
        {justCreated && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setJustCreated(null)}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Nøglen vises kun NU — kopiér den og opbevar sikkert. Du kan ikke se den igen.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                value={justCreated.key}
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                onClick={(e) => (e.target as HTMLInputElement).select?.()}
              />
              <Button size="small" startIcon={<ContentCopyIcon />}
                onClick={() => copy(justCreated.key, 'Nøgle kopieret')}>
                Kopiér
              </Button>
            </Box>
          </Alert>
        )}

        {/* Opret ny */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'flex-end' }}>
          <TextField label="Navn" size="small" placeholder="fx Rental-app produktion"
            value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} sx={{ flex: 1 }} />
          <TextField label="Scope" size="small" placeholder="driver-app"
            value={newKeyScope} onChange={(e) => setNewKeyScope(e.target.value)} sx={{ width: 160 }} />
          <Button variant="contained" startIcon={creating ? <CircularProgress size={14} /> : <AddIcon />}
            disabled={creating || !newKeyName.trim()} onClick={create}>
            Opret
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : keys.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 3, textAlign: 'center' }}>
            Ingen API-nøgler endnu for denne organisation.
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Aktive ({activeKeys.length})</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Navn</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Prefix</TableCell>
                  <TableCell>Oprettet</TableCell>
                  <TableCell>Sidst brugt</TableCell>
                  <TableCell align="right">Handling</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeKeys.map(k => (
                  <TableRow key={k.id} hover>
                    <TableCell>{k.name}</TableCell>
                    <TableCell><Chip size="small" label={k.scope} variant="outlined" /></TableCell>
                    <TableCell><code style={{ fontSize: 12 }}>{k.keyPrefix}…</code></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(k.createdAt)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(k.lastUsedAt)}</Typography></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Tilbagekald nøgle">
                        <IconButton size="small" color="error" onClick={() => revoke(k)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {revokedKeys.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
                  Tilbagekaldte ({revokedKeys.length})
                </Typography>
                <Table size="small">
                  <TableBody>
                    {revokedKeys.map(k => (
                      <TableRow key={k.id} sx={{ opacity: 0.55 }}>
                        <TableCell>{k.name}</TableCell>
                        <TableCell><Chip size="small" label={k.scope} variant="outlined" /></TableCell>
                        <TableCell><code style={{ fontSize: 12 }}>{k.keyPrefix}…</code></TableCell>
                        <TableCell><Typography variant="caption">Tilbagekaldt {fmtDate(k.revokedAt)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 3, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Brug:</strong> Send nøglen i HTTP-header <code>x-api-key: dki_…</code> til vores public API
            (fx <code>/api/public-api/v1/…</code>) eller rental-app's integrations-endpoints.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
      </DialogActions>
    </Dialog>
  );
}
